import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { BookOpen, TrendingUp, Layers, ChevronRight } from 'lucide-react';
import BottomNav from '../components/BottomNav';

interface Book {
  id: string;
  title: string;
  author: string;
  status: string;
  card_count: number;
  learned_count: number;
  created_at: string;
}

export default function Home() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBooks: 0,
    totalCards: 0,
    masteredCards: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await api.getBooks();
      const booksData = response.data || [];
      setBooks(booksData);

      // 计算统计数据
      const totalCards = booksData.reduce((sum: number, book: Book) => sum + (book.card_count || 0), 0);
      const masteredCards = booksData.reduce((sum: number, book: Book) => sum + (book.learned_count || 0), 0);

      setStats({
        totalBooks: booksData.length,
        totalCards,
        masteredCards,
      });
    } catch (error) {
      console.error('加载数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartStudy = () => {
    setLocation('/cards');
  };

  const handleBookStudy = (bookId: string) => {
    setLocation(`/cards?bookId=${bookId}`);
  };

  return (
    <div className="min-h-screen bg-[#1a2e3e] pb-24">
      {/* 头部 */}
      <div className="p-6">
        <h1 className="text-3xl font-bold text-white mb-2">书流</h1>
        <p className="text-white/60">
          欢迎回来, {user?.name || '学习者'}
        </p>
      </div>

      {/* 统计卡片 */}
      <div className="px-6 mb-6">
        <div className="grid grid-cols-2 gap-4">
          {/* 已掌握卡片 */}
          <div className="glass rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1">
              {stats.masteredCards}
            </div>
            <div className="text-sm text-white/60">已掌握卡片</div>
          </div>

          {/* 书籍数量 */}
          <div className="glass rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#22a9d9]/20 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-[#22a9d9]" />
              </div>
            </div>
            <div className="text-3xl font-bold text-white mb-1">
              {stats.totalBooks}
            </div>
            <div className="text-sm text-white/60">书籍数量</div>
          </div>
        </div>
      </div>

      {/* 开始刷知识 CTA */}
      <div className="px-6 mb-8">
        <button
          onClick={handleStartStudy}
          disabled={stats.totalCards === 0}
          className="w-full h-28 rounded-2xl bg-gradient-to-r from-[#22a9d9] to-[#6c5ce7] p-6 flex items-center justify-between hover:shadow-glow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="text-left">
            <h3 className="text-xl font-bold text-white mb-2">开始刷知识</h3>
            <p className="text-white/80 text-sm">
              {stats.totalCards > 0 
                ? `${stats.totalCards} 张卡片等待学习`
                : '暂无卡片,请先上传书籍'}
            </p>
          </div>
          <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
            <Layers className="w-7 h-7 text-white" />
          </div>
        </button>
      </div>

      {/* 最近添加 */}
      <div className="px-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">最近添加</h2>
          <button
            onClick={() => setLocation('/library')}
            className="text-sm text-[#22a9d9] hover:underline flex items-center gap-1"
          >
            查看全部
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-white/60">
            加载中...
          </div>
        ) : books.length === 0 ? (
          <div className="glass rounded-2xl p-8 text-center">
            <BookOpen className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/60 mb-6">还没有上传任何书籍</p>
            <button
              onClick={() => setLocation('/upload')}
              className="px-8 h-12 rounded-xl bg-gradient-to-r from-[#22a9d9] to-[#6c5ce7] text-white font-medium hover:shadow-glow transition-shadow"
            >
              上传第一本书
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {books.slice(0, 3).map((book) => (
              <div
                key={book.id}
                className="glass rounded-2xl p-4 flex items-center gap-4"
              >
                {/* 书籍图标 */}
                <div className="w-12 h-16 rounded-xl bg-gradient-to-br from-[#22a9d9] to-[#6c5ce7] flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>

                {/* 书籍信息 */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-medium truncate mb-1">
                    {book.title}
                  </h3>
                  <p className="text-white/60 text-sm mb-2">{book.author}</p>
                  <div className="flex items-center gap-2 text-xs text-white/50">
                    <span>{book.card_count} 张卡片</span>
                    {book.learned_count > 0 && (
                      <>
                        <span>·</span>
                        <span>已学 {book.learned_count}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* 学习按钮 */}
                {book.status === 'completed' && book.card_count > 0 && (
                  <button
                    onClick={() => handleBookStudy(book.id)}
                    className="px-6 h-10 rounded-xl bg-[#22a9d9]/20 text-[#22a9d9] font-medium hover:bg-[#22a9d9]/30 transition-colors"
                  >
                    学习
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
